import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DEFAULT_LOCALE, OG_LOCALE_MAP, type SupportedLocale } from '../config/localeConfig';
import { getLocalizedValue, type LocalizedField } from '../utils/getLocalizedValue';
import { HreflangTags } from '../components/seo/HreflangTags';
import { useLocale } from '../hooks/useLocale';
import resolveImageUrl from '../utils/resolveImageUrl';

type SeoLocalePublishState = Partial<
  Record<
    SupportedLocale,
    {
      isPublished?: boolean;
      isIndexable?: boolean;
    }
  >
>;

type SeoHeadProps = {
  title?: string | LocalizedField<string>;
  description?: string | LocalizedField<string>;
  keywords?: string[];
  image?: string;
  noindex?: boolean;
  pathname?: string;
  localePublishState?: SeoLocalePublishState;
  type?: 'website' | 'article';
  siteName?: string;
  canonical?: string;
  robots?: {
    index?: boolean;
    follow?: boolean;
    nosnippet?: boolean;
    noarchive?: boolean;
    noimageindex?: boolean;
  };
  openGraph?: {
    title?: string;
    description?: string;
    image?: string;
    imageAlt?: string;
    type?: string;
    url?: string;
  };
  twitter?: {
    card?: 'summary' | 'summary_large_image' | 'app' | 'player';
    title?: string;
    description?: string;
    image?: string;
    imageAlt?: string;
    site?: string;
    creator?: string;
  };
  hreflang?:
    | Record<string, string>
    | Array<{
        hreflang?: string;
        href?: string;
      }>;
  customHeadTags?: string;
};

function normalizeSiteUrl(value?: string) {
  const fallback = 'https://www.pearlvoyages.com';
  const raw = String(value || fallback).replace(/\/+$/, '');
  try {
    const parsed = new URL(raw);
    if (parsed.hostname === 'pearlvoyages.com') parsed.hostname = 'www.pearlvoyages.com';
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return fallback;
  }
}

const SITE_URL = normalizeSiteUrl(import.meta.env.VITE_PUBLIC_SITE_URL);
const DEFAULT_OG_IMAGE = 'https://cdn.pearlvoyages.com/home-page/kidepovalley.jpg';

function absoluteUrl(value?: string) {
  if (!value) return undefined;
  const raw = String(value || '').trim();
  if (!raw) return undefined;

  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      if (parsed.hostname === 'pearlvoyages.com') parsed.hostname = 'www.pearlvoyages.com';
      return parsed.toString();
    }
  } catch {
    return raw;
  }

  return `${SITE_URL}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

function absoluteImageUrl(value?: string) {
  const resolved = resolveImageUrl(value || DEFAULT_OG_IMAGE);
  return absoluteUrl(resolved || DEFAULT_OG_IMAGE);
}

function imageMimeType(value?: string) {
  const clean = String(value || '').split('?')[0].toLowerCase();
  if (clean.endsWith('.webp')) return 'image/webp';
  if (clean.endsWith('.png')) return 'image/png';
  if (clean.endsWith('.jpg') || clean.endsWith('.jpeg')) return 'image/jpeg';
  return undefined;
}

function normalizeHreflangEntries(
  hreflang?: SeoHeadProps['hreflang']
): Array<[string, string]> {
  if (!hreflang) return [];

  if (Array.isArray(hreflang)) {
    return hreflang
      .map((item) => [String(item?.hreflang || '').trim(), String(item?.href || '').trim()] as const)
      .filter(([lang, href]) => Boolean(lang) && Boolean(href));
  }

  return Object.entries(hreflang)
    .map(([lang, href]) => [String(lang).trim(), String(href || '').trim()] as const)
    .filter(([lang, href]) => Boolean(lang) && Boolean(href));
}

export default function SeoHead({
  title,
  description,
  keywords = [],
  image,
  noindex = false,
  pathname,
  localePublishState,
  type = 'website',
  siteName = 'Pearl Voyages Travel',
  canonical,
  robots = {},
  openGraph = {},
  twitter = {},
  hreflang = {},
}: SeoHeadProps) {
  const { locale } = useLocale();
  const location = useLocation();

  const resolvedPathname = pathname || location.pathname;

  const resolvedTitle = useMemo(() => {
    if (typeof title === 'string') return title;
    return getLocalizedValue(title, locale) || getLocalizedValue(title, DEFAULT_LOCALE) || siteName;
  }, [title, locale, siteName]);

  const resolvedDescription = useMemo(() => {
    if (typeof description === 'string') return description;
    return getLocalizedValue(description, locale) || getLocalizedValue(description, DEFAULT_LOCALE) || '';
  }, [description, locale]);

  const ogLocale = OG_LOCALE_MAP[locale] || OG_LOCALE_MAP.en;
  const selectedImage = image || openGraph.image || twitter.image || DEFAULT_OG_IMAGE;
  const ogImage = absoluteImageUrl(selectedImage);
  const canonicalUrl = absoluteUrl(canonical || resolvedPathname);
  const resolvedOgTitle = openGraph.title || resolvedTitle;
  const resolvedOgDescription = openGraph.description || resolvedDescription;
  const resolvedTwitterTitle = twitter.title || resolvedTitle;
  const resolvedTwitterDescription = twitter.description || resolvedDescription;
  const robotsParts: string[] = [];
  robotsParts.push(robots.index === false || noindex ? 'noindex' : 'index');
  robotsParts.push(robots.follow === false ? 'nofollow' : 'follow');
  if (robots.nosnippet) robotsParts.push('nosnippet');
  if (robots.noarchive) robotsParts.push('noarchive');
  if (robots.noimageindex) robotsParts.push('noimageindex');
  const robotsString = robotsParts.join(', ');
  const ogUrl = absoluteUrl(openGraph.url) || canonicalUrl;
  const twitterCard = twitter.card || (ogImage ? 'summary_large_image' : 'summary');
  const hreflangEntries = normalizeHreflangEntries(hreflang);

  return (
    <>
      <Helmet>
        <title>{resolvedTitle}</title>

        {resolvedDescription ? (
          <meta name="description" content={resolvedDescription} />
        ) : null}
        {keywords.length > 0 ? (
          <meta name="keywords" content={keywords.join(', ')} />
        ) : null}

        <meta name="robots" content={robotsString} />

        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

        <meta property="og:type" content={type} />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:title" content={resolvedOgTitle} />
        {resolvedOgDescription ? (
          <meta property="og:description" content={resolvedOgDescription} />
        ) : null}
        {ogUrl ? <meta property="og:url" content={ogUrl} /> : null}
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        {ogImage ? <meta property="og:image:secure_url" content={ogImage} /> : null}
        {ogImage && imageMimeType(ogImage) ? <meta property="og:image:type" content={imageMimeType(ogImage)} /> : null}
        {ogImage ? <meta property="og:image:width" content="1200" /> : null}
        {ogImage ? <meta property="og:image:height" content="630" /> : null}
        {openGraph.imageAlt ? <meta property="og:image:alt" content={openGraph.imageAlt} /> : null}

        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:title" content={resolvedTwitterTitle} />
        {resolvedTwitterDescription ? (
          <meta name="twitter:description" content={resolvedTwitterDescription} />
        ) : null}
        {ogImage ? <meta name="twitter:image" content={absoluteImageUrl(twitter.image || selectedImage)} /> : null}
        {twitter.imageAlt ? <meta name="twitter:image:alt" content={twitter.imageAlt} /> : null}
        {twitter.site ? <meta name="twitter:site" content={twitter.site} /> : null}
        {twitter.creator ? <meta name="twitter:creator" content={twitter.creator} /> : null}

        {hreflangEntries.map(([lang, href]) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={href} />
        ))}
      </Helmet>

      {!noindex && !hreflangEntries.length ? (
        <HreflangTags
          locale={locale}
          pathname={resolvedPathname}
          localePublishState={localePublishState}
        />
      ) : null}
    </>
  );
}
